import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { LogOut, User, Bell, Shield, ChevronDown, Menu, Scale } from 'lucide-react';
import { getInitials } from '../../lib/utils';
import { TokenBalance } from '../TokenBalance';
import { BRAND } from '../../config/branding';

export const Header = ({ title, onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 h-14 lg:h-14 bg-[#0F172A]/95 backdrop-blur-xl border-b border-[#B69D74]/10 flex items-center justify-between px-4 lg:px-6 z-40 safe-top">
      {/* Gauche: Menu hamburger (mobile) + Titre */}
      <div className="flex items-center gap-3">
        {/* Bouton menu hamburger - visible uniquement sur mobile */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 -ml-2 text-[#94A3B8] hover:text-white hover:bg-white/5 rounded-lg transition-colors"
          aria-label="Ouvrir le menu"
        >
          <Menu className="w-6 h-6" />
        </button>
        
        {/* Logo sur mobile (remplace le titre) */}
        <div className="flex items-center gap-2 lg:hidden">
          <img src="/logo.png" alt={BRAND.name} className="w-6 h-6" />
          <span className="text-base font-heading font-semibold text-[#F8FAFC]">
            {BRAND.name}
          </span>
        </div>
        
        {/* Titre sur desktop */}
        <h1 className="hidden lg:block text-base font-heading font-semibold text-[#F8FAFC]">
          {title}
        </h1>

        {/* Mention discrète version test (gatée par REACT_APP_ENV=test) */}
        {BRAND.isTest && (
          <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide bg-amber-500/15 text-amber-400 border border-amber-500/30">
            version test
          </span>
        )}
      </div>

      {/* Droite: Actions */}
      <div className="flex items-center gap-2 lg:gap-3">
        {/* Solde de tokens */}
        <TokenBalance />

        {/* Notifications */}
        <Button 
          variant="ghost" 
          size="icon" 
          className="text-[#94A3B8] hover:text-[#B69D74] hover:bg-white/5 h-10 w-10 lg:h-9 lg:w-9"
          data-testid="notifications-btn"
        >
          <Bell className="w-5 h-5 lg:w-4 lg:h-4" strokeWidth={1.5} />
        </Button>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="flex items-center gap-2 px-2 lg:px-3 hover:bg-white/5 h-10 lg:h-9"
              data-testid="user-menu-btn"
            >
              <Avatar className="w-8 h-8 lg:w-7 lg:h-7">
                {user?.avatar && <AvatarImage src={user.avatar} alt={`${user.prenom} ${user.nom}`} />}
                <AvatarFallback className="bg-[#B69D74]/20 text-[#B69D74] text-xs font-bold">
                  {user ? getInitials(user.nom, user.prenom) : '??'}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium hidden lg:block text-[#94A3B8]">
                {user ? `${user.prenom} ${user.nom}` : ''}
              </span>
              <ChevronDown className="w-3 h-3 text-[#94A3B8] hidden lg:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 bg-[#1E293B] border-[#B69D74]/20">
            <DropdownMenuItem 
              onClick={() => navigate('/settings')}
              data-testid="menu-profile"
              className="text-sm cursor-pointer text-[#94A3B8] hover:bg-white/5 hover:text-[#B69D74] focus:bg-white/5 focus:text-[#B69D74]"
            >
              <User className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Mon profil
            </DropdownMenuItem>
            {user?.role === 'admin' && (
              <>
                <DropdownMenuSeparator className="bg-[#B69D74]/10" />
                <DropdownMenuItem 
                  onClick={() => navigate('/admin')}
                  data-testid="menu-admin"
                  className="text-sm cursor-pointer text-[#94A3B8] hover:bg-white/5 hover:text-[#B69D74] focus:bg-white/5 focus:text-[#B69D74]"
                >
                  <Shield className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Administration
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator className="bg-[#B69D74]/10" />
            <DropdownMenuItem 
              onClick={handleLogout}
              className="text-sm cursor-pointer text-red-400 hover:bg-red-500/10 hover:text-red-400 focus:bg-red-500/10 focus:text-red-400"
              data-testid="menu-logout"
            >
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
